import React from "react";

import { useRecordContext, Button, useNotify } from "react-admin";

import { httpClient } from "../lib/httpClient";
import MuiTextField from "@mui/material/TextField";

import { bwColours } from "../lib/theme";
import * as d3 from "d3";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { FermentPlot, densityPlotConfigs } from "../charts/ferment";
import { custInfo } from "../lib/customer";
import {
  cToF,
  fToC,
  prefersFahrenheit,
  snapToF,
  temperatureUnit,
} from "../lib/temperature";

export const FermentExplorer = ({ data }) => {
  const defaultStartTime = data.densSeries.length - 1;
  const defaultTargetTemp = data.temperatureSeries[defaultStartTime];
  const defaultTau = 20;

  const [running, setRunning] = React.useState<boolean>(false);
  const [startTime, setStartTime] = React.useState<number | null>(
    defaultStartTime,
  );
  const [targetTemperature, setTargetTemperature] = React.useState<
    number | null
  >(defaultTargetTemp);
  const [tau, setTau] = React.useState<number | null>(defaultTau);

  // Temperatures are stored in °C; convert to °F only for presentation.
  const useF = prefersFahrenheit();
  const displayTargetTemp = (celsius: number) =>
    useF ? snapToF(celsius) : celsius;

  // The target temperature input keeps its raw text while focused so the
  // user can type a full value in the display unit (partial input like "6"
  // while typing "68" must not be re-parsed and clamped on every keystroke).
  const [targetTempEditing, setTargetTempEditing] = React.useState(false);
  const [targetTempRaw, setTargetTempRaw] = React.useState("");
  const [simResult, setSimResult] = React.useState<any>({});

  const notify = useNotify();
  const record = useRecordContext();

  if (!record) return null;

  if (!data.densSeries) return;

  const densityUnit = custInfo.customerPreferences.densityUnit;
  const densityPlotConfig =
    densityPlotConfigs[densityUnit as keyof typeof densityPlotConfigs] ??
    densityPlotConfigs["°Bx"];

  const recordDescription = "Ferment ID " + record.id;

  const handleSimulate = () => {
    if (startTime === null) setStartTime(defaultStartTime);
    if (targetTemperature === null) setTargetTemperature(defaultTargetTemp);
    if (tau === null) setTau(defaultTau);

    setRunning(true);
    setSimResult({});
    httpClient(
      "/api/v2/c/" + record.customerID + "/ferments/" + record.id + "/simulate",
      {
        method: "POST",
        body: JSON.stringify({
          finalTemp:
            targetTemperature === null ? defaultTargetTemp : targetTemperature,
          tau: tau === null ? defaultTau : tau,
          currentTimeHours: startTime === null ? defaultStartTime : startTime,
        }),
      },
    )
      .then(async (resp) => {
        setRunning(false);
        if (resp.ok) {
          notify(recordDescription + " simulation complete", {
            type: "success",
          });
          setSimResult(await resp.json());
          ///////
        } else {
          notify(recordDescription + " simulation failed", {
            type: "error",
          });
          console.log(resp);
        }
      })
      .catch((error) => {
        notify(recordDescription + " simulation failed", {
          type: "error",
        });
        console.log(error);
      });
  };

  // The model rix density curve for the observed period
  const modelDens =
    record &&
    record.data &&
    record.data.mvModelling &&
    record.data.mvModelling.body &&
    record.data.mvModelling.body.ModelResults &&
    record.data.mvModelling.body.ModelResults.DensBest
      ? record.data.mvModelling.body.ModelResults.DensBest.map((y, x) => ({
          // This dataset is every 2 hours
          x: x * 2,
          y: y,
        }))
      : [];

  // The density curve of the forward-simulated period
  const simDens =
    simResult && simResult.body && simResult.body.densFuture
      ? simResult.body.densFuture.map((dens, idx) => ({
          x: simResult.body.timeHourly[idx],
          y: dens,
        }))
      : null;

  // If both density curves are present, splice them together
  let predictionSeries = null;
  if (modelDens.length > 0 && !!simDens && simDens.length > 0) {
    const spliceIndex = simDens[0].x;
    const trimmedModelDens = modelDens.filter((el) => el.x < spliceIndex);
    predictionSeries = trimmedModelDens.concat(simDens);
  } else {
    predictionSeries = simDens;
  }

  const simTemperature =
    simResult && simResult.body && simResult.body.tempFuture
      ? simResult.body.tempFuture.map((temperature, idx) => ({
          x: simResult.body.timeHourly[idx],
          y: temperature,
        }))
      : null;

  return (
    <>
      <div
        style={{
          backgroundColor: bwColours.primary.offwhite,
          padding: 20,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <MuiTextField
          label="Start Time (Hours)"
          value={startTime}
          onChange={(event) => {
            // Special case to allow the user to delete the value temporarily
            // so they can type in a new one.
            if (event.target.value === "") {
              setStartTime(null);
              return;
            }
            const newValue = parseFloat(event.target.value);
            setStartTime(
              !isFinite(newValue) || newValue < 0 || newValue > defaultStartTime
                ? defaultStartTime
                : newValue,
            );
          }}
          disabled={running}
        ></MuiTextField>
        <MuiTextField
          label={useF ? "Target Temperature (°F)" : "Target Temperature (°C)"}
          value={
            targetTempEditing
              ? targetTempRaw
              : targetTemperature !== null
                ? displayTargetTemp(targetTemperature)
                : ""
          }
          onChange={(event) => {
            setTargetTempRaw(event.target.value);
          }}
          onFocus={() => {
            setTargetTempEditing(true);
            setTargetTempRaw(
              targetTemperature !== null
                ? String(displayTargetTemp(targetTemperature))
                : "",
            );
          }}
          onBlur={() => {
            setTargetTempEditing(false);
            if (targetTempRaw === "") {
              setTargetTemperature(null);
              return;
            }
            let newValue = parseFloat(targetTempRaw);
            if (useF) newValue = fToC(newValue);
            setTargetTemperature(
              !isFinite(newValue) || newValue < 0 || newValue > 100
                ? defaultTargetTemp
                : newValue,
            );
          }}
          disabled={running}
        ></MuiTextField>
        <MuiTextField
          label="Temperature Response (Tau)"
          value={tau}
          onChange={(event) => {
            // Special case to allow the user to delete the value temporarily
            // so they can type in a new one.
            if (event.target.value === "") {
              setTau(null);
              return;
            }
            const newValue = parseFloat(event.target.value);
            setTau(
              !isFinite(newValue) || newValue < 0 || newValue > 100
                ? defaultTau
                : newValue,
            );
          }}
          disabled={running}
        ></MuiTextField>
        <Button
          label="Simulate"
          size="medium"
          onClick={handleSimulate}
          disabled={running}
        >
          <RotateLeftIcon />
        </Button>
      </div>
      <FermentPlot
        startTime={new Date(data.startTime)}
        xMax={d3.max([
          3 * 24,
          data.densSeries.length - 1,
          data.predictedDuration + 3 * 24,
        ])}
        dataSeries={data.densSeries?.map((y: number, x: number) => ({
          x: x,
          y: densityPlotConfig.convertValue(y),
        }))}
        predictionSeries={predictionSeries?.map((point) => ({
          x: point.x,
          y: densityPlotConfig.convertValue(point.y),
        }))}
        units={densityPlotConfig.yConfig.units}
        yMin={densityPlotConfig.yConfig.yMin}
        yMax={densityPlotConfig.yConfig.yMax}
        yLabel={densityPlotConfig.yConfig.yLabel}
        colour={bwColours.accent.green}
        mini={false}
        referenceSeries={undefined}
        setPoint={undefined}
        precision={densityPlotConfig.yConfig.precision}
      />
      <FermentPlot
        startTime={new Date(data.startTime)}
        xMax={d3.max([
          3 * 24,
          data.temperatureSeries.length - 1,
          data.predictedDuration + 3 * 24,
        ])}
        dataSeries={data.temperatureSeries.map((y, x) => ({
          x: x,
          y: useF ? cToF(y) : y,
        }))}
        predictionSeries={simTemperature?.map((point) => ({
          x: point.x,
          y: useF ? cToF(point.y) : point.y,
        }))}
        units={temperatureUnit()}
        yMin={useF ? cToF(5) : 5}
        yMax={useF ? cToF(25) : 25}
        yLabel={`Temperature (${temperatureUnit()})`}
        colour={bwColours.accent.blue}
        mini={false}
        referenceSeries={undefined}
        setPoint={undefined}
      />
    </>
  );
};
